import isEqual from 'lodash/isEqual';
import React, { useCallback, useState } from 'react';
import { Form } from 'react-final-form';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { localize } from 'common/utils';
import { firstOrNull } from 'common/utils/array';
import { convertMonetaryIntToDecimal } from 'common/utils/currencyDecimals';
import { useAdCampaignTranslationKeys } from 'modules/campaigns/ad-campaign/hooks/useAdCampaignTranslationKeys';
import { reportEvent } from 'modules/campaigns/behavioral';
import { useCampaignModuleContext } from 'modules/campaigns/context';
import {
  CampaignStatus,
  AdCampaignFormValues,
  AdCampaignBudgetFormValues,
  AdCampaignConfigFormValues,
  AdsFreeTrialConfig,
} from 'modules/campaigns/types';
import { shakingAnimationMixin } from 'modules/campaigns/utils/shakingCssAnimation';

import { ExistingCampaignAgreement } from './ExistingCampaignAgreement';
import { NewCampaignAgreementDetails } from './NewCampaignAgreement';
import { hasCampaignStarted } from './utils/campaign';
import { preserveChangedValues } from './utils/common';
import { FormValidationError, validateForm } from './utils/form';
import CampaignFormLayout from '../../../components/CampaignFormLayout';
import CopyCampaignIdButton from '../../../components/CopyCampaignIdButton';
import { CampaignFormUnsavedChangesModal } from '../../../components/modals/CampaignFormUnsavedChangesModal/CampaignFormUnsavedChangesModal';
import { TestIds } from '../../../utils/testUtils';
import { AdCampaignActionsFooter } from '../AdCampaignActionsFooter';
import { AdCampaignBudgetFormItem } from '../budget-section/AdCampaignBudgetFormItem';
import { FreeTrialFormItem } from '../free-trials/form-components/free-trials-section/FreeTrialFormItem';
import { AdCampaignPreview } from '../preview-section/AdCampaignPreview';
import { AdCampaignScheduleSectionItem } from '../schedule-section/AdCampaignScheduleSectionItem';

export type AdCampaignFormProps = {
  campaignAuthor?: string;
  adsConfig: AdCampaignConfigFormValues;
  campaignId?: string;
  campaignStatus?: CampaignStatus;
  initialValues: AdCampaignFormValues;
  isLoading: boolean;
  onSubmit?: (values: AdCampaignFormValues) => void;
  onUpdate?: (values: Partial<AdCampaignFormValues>) => void;
  onCancelEdit?: () => void;
  adsFreeTrialConfig?: AdsFreeTrialConfig;
};

const StyledRoot = styled.div`
  max-width: 1120px;
  width: 100%;
  margin: 0 auto;
`;

const StyledAdCampaignScheduleSectionItem = styled(AdCampaignScheduleSectionItem)<{ isShaking: boolean }>`
  ${({ isShaking }) => (isShaking ? shakingAnimationMixin : 'animation: none')}
`;

export const AdCampaignForm = ({
  campaignId,
  campaignAuthor,
  campaignStatus,
  adsConfig,
  initialValues,
  isLoading,
  onSubmit,
  onUpdate,
  onCancelEdit,
  adsFreeTrialConfig,
}: AdCampaignFormProps) => {
  const translationKeysPerCountry = useAdCampaignTranslationKeys();
  const { formatMessage, locale } = useIntl();
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = useState<boolean>(false);

  // Campaign ID doesn't exist for campaigns that are being created
  const isNewCampaign = !campaignId;
  const isCampaignActive =
    !isNewCampaign &&
    !!campaignStatus &&
    hasCampaignStarted({
      campaignStatus,
      startDate: initialValues.startDate,
    });

  const isExistingFreeTrial = initialValues.isFreeTrial && !isNewCampaign;

  const [showAgreementsErrorAnimation, setShowAgreementsErrorAnimation] = useState<boolean>(false);
  const [showScheduleErrorAnimation, setShowScheduleErrorAnimation] = useState(false);
  const { venues } = useCampaignModuleContext();
  const venueToPreview = firstOrNull(venues);

  const handleSubmitCampaign = useCallback(
    (values: AdCampaignFormValues) => {
      const handleInvalidTermsAndConditions = () => {
        setShowAgreementsErrorAnimation(false);
        // Timeout to prevent React from batching state setters
        setTimeout(() => setShowAgreementsErrorAnimation(true), 0);
      };

      const handleInvalidEndDate = () => {
        setShowScheduleErrorAnimation(false);
        // Timeout to prevent React from batching state setters
        setTimeout(() => setShowScheduleErrorAnimation(true), 0);
      };

      const isUpdatingCampaign = !!campaignId && onUpdate;

      if (isUpdatingCampaign) {
        const editedValues = preserveChangedValues(values, initialValues);
        return onUpdate(editedValues);
      }

      const errors = validateForm({ values, adsFreeTrialConfig });

      if (errors[FormValidationError.INVALID_TERMS_AND_CONDITIONS]) {
        handleInvalidTermsAndConditions();
      }

      if (errors[FormValidationError.INVALID_END_DATE]) {
        handleInvalidEndDate();
      }

      const hasFormErrors = !Object.values(errors).every(errorExists => !errorExists);

      if (hasFormErrors || !onSubmit) {
        return;
      }

      reportEvent('NEW_AD_CAMPAIGN_CREATE_CAMPAIGN');
      return onSubmit({ ...values, campaignId });
    },
    [campaignId, onUpdate, adsFreeTrialConfig, onSubmit, initialValues],
  );

  const {
    budgetOptions,
    currency,
    maximumCustomBidAmount,
    minimumBidAmount,
    minimumBudgetAmount,
    maximumBudgetAmount,
  } = adsConfig;

  return (
    <StyledRoot data-test-id="ads-form">
      <Form<AdCampaignFormValues> keepDirtyOnReinitialize onSubmit={handleSubmitCampaign} initialValues={initialValues}>
        {({ handleSubmit, form, values }) => {
          // using 'dirty' prop does not change for some reason when making changes to schedule so instead use 'isEqual' to compare the values
          const isDirty = !isEqual(values, initialValues);
          return (
            <CampaignFormLayout
              preview={
                venueToPreview && (
                  <AdCampaignPreview
                    venue={{
                      name: localize(venueToPreview.name, locale),
                      description: localize(venueToPreview.short_description, locale),
                      imageUrl: venueToPreview.listimage,
                    }}
                  />
                )
              }
              onFormSubmit={handleSubmit}
              tooltip="campaigns.sidebar-preview.ads.tooltip"
              isFloatingBannerEnabled
              infoBannerContent={{
                title: formatMessage({ id: 'campaigns.ads.info-banner.title' }),
                text: formatMessage({ id: translationKeysPerCountry.infoBannerText }),
                campaignId: campaignId ?? 'NewCampaign',
              }}
            >
              <CampaignFormUnsavedChangesModal
                isOpen={isUnsavedChangesModalOpen}
                setIsOpen={setIsUnsavedChangesModalOpen}
              />

              <FreeTrialFormItem isExistingFreeTrial={isExistingFreeTrial} adsFreeTrialConfig={adsFreeTrialConfig} />

              <AdCampaignBudgetFormItem
                initialValues={{
                  budgetAmount: values.budgetAmount,
                  budgetType: values.budgetType,
                  bidStrategy: values.bidStrategy,
                  customBidAmount: values.customBidAmount,
                }}
                budgetOptions={[...budgetOptions, { type: 'custom', value: 0 }]}
                campaignId={campaignId}
                isDisabled={campaignStatus === CampaignStatus.COMPLETED}
                hasCampaignStarted={isCampaignActive}
                onUpdate={(formValues: AdCampaignBudgetFormValues) => {
                  form.batch(() => {
                    form.change('budgetAmount', formValues.budgetAmount);
                    form.change('bidStrategy', formValues.bidStrategy);
                    form.change('customBidAmount', formValues.customBidAmount);
                    form.change('budgetType', formValues.budgetType);
                  });
                }}
                maxBidAmount={convertMonetaryIntToDecimal(maximumCustomBidAmount, currency)}
                minBidAmount={convertMonetaryIntToDecimal(minimumBidAmount, currency)}
                minWeeklyBudget={convertMonetaryIntToDecimal(minimumBudgetAmount, currency)}
                maxWeeklyBudget={convertMonetaryIntToDecimal(maximumBudgetAmount, currency)}
                isFreeTrial={values.isFreeTrial}
              />
              <StyledAdCampaignScheduleSectionItem
                isShaking={showScheduleErrorAnimation}
                campaignId={campaignId}
                initialValues={{
                  startDate: values.startDate,
                  endDate: values.endDate,
                  scheduleStrategy: values.scheduleStrategy,
                }}
                campaignStatus={campaignStatus}
                onUpdate={values => {
                  form.batch(() => {
                    form.change('startDate', values.startDate);
                    form.change('endDate', values.endDate);
                    form.change('scheduleStrategy', values.scheduleStrategy);
                  });
                }}
                isExistingFreeTrial={isExistingFreeTrial}
                hasCampaignStarted={isCampaignActive}
                isFreeTrialSelected={values.isFreeTrial}
                adsFreeTrialConfig={form.getState().values.isFreeTrial ? adsFreeTrialConfig : undefined}
              />
              {!isNewCampaign ? (
                <NewCampaignAgreementDetails campaignAuthor={campaignAuthor} />
              ) : (
                <ExistingCampaignAgreement showErrorAnimation={showAgreementsErrorAnimation} />
              )}
              <AdCampaignActionsFooter
                campaignId={campaignId}
                isDirty={isDirty}
                isLoading={isLoading}
                onSubmit={handleSubmit}
                onCancel={onCancelEdit}
              />
            </CampaignFormLayout>
          );
        }}
      </Form>

      {!isNewCampaign && <CopyCampaignIdButton dataTestId={TestIds.FORM.ID.WRAPPER} campaignId={campaignId} />}
    </StyledRoot>
  );
};
